import { useDesignConfirm } from '#design-tokenomics/composables/useDesignConfirm'
import { createLeaveGuard } from '#design-tokenomics/utils/leave-guard'
import { onBeforeRouteLeave } from 'vue-router'

const LEAVE_PROMPT = 'Are you sure you want to leave without saving your latest changes?'

const sources = new Set<() => boolean>()

export function registerUnsavedWork(isDirty: () => boolean) {
  sources.add(isDirty)

  onScopeDispose(() => {
    sources.delete(isDirty)
  })
}

export function hasUnsavedWork() {
  return [...sources].some(isDirty => isDirty())
}

/**
 * The tool's single route leave guard. Hosted by whatever is mounted for the whole page, so every
 * design route is covered whether or not it has a form of its own.
 */
export function useDesignLeaveGuard() {
  const { confirm } = useDesignConfirm()

  const leave = createLeaveGuard({
    isDirty: hasUnsavedWork,
    ask: () => confirm({
      title: LEAVE_PROMPT,
      description: 'Your unsaved edits will be lost.',
      confirmLabel: 'Leave without saving',
      cancelLabel: 'Keep editing',
    }),
  })

  onBeforeRouteLeave(() => leave.allowLeave())

  // `afterEach` fires once the whole chain settles - the router skips it for a leg a middleware
  // redirected away from. It hands back an unregister function, which the page has to call or
  // every visit leaves another guard on the router.
  onScopeDispose(useRouter().afterEach(leave.settle))
}
